import { useState, useEffect, useCallback, useMemo } from 'react';
import { useFrappeGetCall, useFrappeAuth } from 'frappe-react-sdk';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import {
    Calendar, Clock, ChevronRight, Activity,
    RefreshCw, FileText, LayoutGrid
} from 'lucide-react';
import Navbar from '../components/Navbar';
import UserActivity from '../components/UserActivity';
import SessionReportAI from '../components/SessionReportAI';
import OnboardingModal from '../components/OnboardingModal';
import { isCurrentUserAdmin } from '../utils/api';
import { useUserRoles } from '../hooks/useUserRoles';
import { useOnboarding } from '../hooks/useOnboarding';

interface Session {
    name: string;
    title: string;
    description: string;
    session_date: string;
    status: string;
    student_1_name?: string;
    student_2_name?: string;
    video_count?: number;
}

const Dashboard = () => {
    // --- State & Hooks ---
    const [sessions, setSessions] = useState<Session[]>([]);

    // Tab State
    const [activeTab, setActiveTabInternal] = useState<'dashboard' | 'sessions' | 'history' | 'reports'>(() => {
        try {
            const savedTab = sessionStorage.getItem('surgical_training_active_tab');
            if (savedTab && ['dashboard', 'sessions', 'history', 'reports'].includes(savedTab)) {
                return savedTab as 'dashboard' | 'sessions' | 'history' | 'reports';
            }
        } catch (error) {
            console.warn('Failed to restore tab from sessionStorage:', error);
        }
        return 'dashboard';
    });

    const setActiveTab = useCallback((newTab: 'dashboard' | 'sessions' | 'history' | 'reports') => {
        sessionStorage.setItem('surgical_training_active_tab', newTab);
        setActiveTabInternal(newTab);
    }, []);

    // Sort State
    const [sortBy, setSortBy] = useState<'date' | 'type'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Onboarding
    const { isOnboardingActive, hasCompletedOnboarding } = useOnboarding();
    const [showOnboardingModal, setShowOnboardingModal] = useState(false);

    // Auth & Roles
    const { currentUser: authUser } = useFrappeAuth();
    const { hasDoctor } = useUserRoles();
    const isAdmin = isCurrentUserAdmin(authUser || undefined);

    // Data Fetching
    const shouldShowAllSessions = isAdmin || hasDoctor;
    const apiEndpoint = shouldShowAllSessions
        ? 'surgical_training.api.session.get_sessions'
        : 'surgical_training.api.session_assignment.get_user_assigned_sessions';

    const { data, error, isValidating, mutate: refreshSessions } = useFrappeGetCall(apiEndpoint);

    // --- Effects ---

    useEffect(() => {
        if (data && typeof data === 'object' && data.message) {
            const responseData = data.message;
            if (responseData && responseData.message === 'Success' && Array.isArray(responseData.data)) {
                setSessions(responseData.data);
            }
        }
        if (error) {
            toast.error('Failed to load sessions');
        }
    }, [data, error]);

    useEffect(() => {
        if (isOnboardingActive && !hasCompletedOnboarding) {
            const timer = setTimeout(() => setShowOnboardingModal(true), 500);
            return () => clearTimeout(timer);
        }
    }, [isOnboardingActive, hasCompletedOnboarding]);

    // --- Handlers ---

    const handleRefreshSessions = useCallback(async () => {
        try {
            await refreshSessions();
            toast.success('Sessions refreshed');
        } catch (e) {
            toast.error('Failed to refresh');
        }
    }, [refreshSessions]);

    const handleSortChange = useCallback((newSortBy: 'date' | 'type') => {
        if (sortBy === newSortBy) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(newSortBy);
            setSortOrder('desc');
        }
    }, [sortBy]);

    // --- Helpers ---

    const parseSessionTitle = useCallback((title: string) => {
        const parts = title.split(' ');
        const datePart = parts[0];
        const typePart = parts.slice(1).join(' ');
        return {
            date: datePart,
            type: typePart || 'General',
            parsedDate: new Date(datePart.replace(/\//g, '-'))
        };
    }, []);

    const sortedSessions = useMemo(() => {
        if (!sessions.length) return sessions;
        return [...sessions].sort((a, b) => {
            const aData = parseSessionTitle(a.title);
            const bData = parseSessionTitle(b.title);
            let comparison = 0;
            if (sortBy === 'date') {
                comparison = aData.parsedDate.getTime() - bData.parsedDate.getTime();
            } else {
                comparison = aData.type.localeCompare(bData.type);
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [sessions, sortBy, sortOrder, parseSessionTitle]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getStatusStyle = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'completed': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
            default: return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    // --- Render Sections ---

    const renderHeader = () => (
        <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                        Welcome back{authUser ? `, ${authUser}` : ''}
                    </h1>
                    <p className="text-gray-500 mt-1 text-lg">
                        Track your progress and manage your training sessions.
                    </p>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white p-1 rounded-xl border border-gray-200 shadow-sm inline-flex">
                    {[
                        { id: 'dashboard', label: 'Overview', icon: LayoutGrid },
                        ...(hasDoctor ? [{ id: 'sessions', label: 'Assessments', icon: FileText }] : []),
                        { id: 'history', label: 'History', icon: Activity },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id as any)}
                            className={`
                flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                ${activeTab === tab.id
                                    ? 'bg-blue-600 text-white shadow-md ring-1 ring-blue-600'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }
              `}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderControls = () => (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    {activeTab === 'dashboard' && 'Training Overview'}
                    {activeTab === 'sessions' && 'Assessment Reports'}
                    {activeTab === 'history' && 'Activity Logs'}
                    <span className="text-xs font-normal px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full border border-gray-200">
                        {activeTab === 'dashboard' ? sessions.length : 0}
                    </span>
                </h2>
            </div>

            {activeTab === 'dashboard' && (
                <div className="flex items-center gap-2">
                    <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                        <span className="px-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Sort by</span>
                        <div className="h-4 w-px bg-gray-200 mx-1"></div>
                        <button
                            onClick={() => handleSortChange('date')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${sortBy === 'date' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            Date
                        </button>
                        <button
                            onClick={() => handleSortChange('type')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${sortBy === 'type' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            Type
                        </button>
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={handleRefreshSessions}
                        disabled={isValidating}
                        className="h-10 w-10 bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
                    >
                        <RefreshCw size={16} className={`${isValidating ? 'animate-spin text-blue-600' : 'text-gray-600'}`} />
                    </Button>
                </div>
            )}
        </div>
    );

    const renderSessionCard = (session: Session, index: number) => {
        const { type, parsedDate } = parseSessionTitle(session.title);

        return (
            <Card
                key={session.name}
                className="group relative flex flex-col overflow-hidden border-gray-200 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300"
                style={{ animationDelay: `${index * 50}ms` }}
            >
                <div className="absolute top-0 left-0 w-1 h-full bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />

                <CardHeader className="pb-3 pt-5 px-5">
                    <div className="flex justify-between items-start gap-4">
                        <div>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 mb-3 border border-blue-100">
                                {type}
                            </span>
                            <h3 className="text-lg font-bold text-gray-900 leading-tight group-hover:text-blue-700 transition-colors">
                                {session.title}
                            </h3>
                        </div>
                        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${getStatusStyle(session.status)}`}>
                            {session.status || 'Active'}
                        </span>
                    </div>
                </CardHeader>

                <CardContent className="flex-grow px-5 pb-4">
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4 border-b border-gray-100 pb-4">
                        <div className="flex items-center gap-1.5">
                            <Calendar size={14} className="text-gray-400" />
                            <span>{formatDate(session.session_date)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Clock size={14} className="text-gray-400" />
                            <span>45m</span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: session.description || '<span class="text-gray-400 italic">No description available yet.</span>' }}
                        />

                        {(session.student_1_name || session.student_2_name) && (
                            <div className="flex items-center gap-2 pt-2">
                                <div className="flex -space-x-2">
                                    {session.student_1_name && (
                                        <div className="h-7 w-7 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-emerald-700" title={session.student_1_name}>
                                            {session.student_1_name.charAt(0)}
                                        </div>
                                    )}
                                    {session.student_2_name && (
                                        <div className="h-7 w-7 rounded-full bg-indigo-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-indigo-700" title={session.student_2_name}>
                                            {session.student_2_name.charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <span className="text-xs text-gray-600 font-medium">+ Students Assigned</span>
                            </div>
                        )}
                    </div>
                </CardContent>

                <CardFooter className="px-5 pb-5 pt-0 mt-auto">
                    <Button
                        asChild
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-300 justify-between group/btn h-11 border-transparent"
                    >
                        <Link to={`/session/${session.name}`}>
                            <span className="font-semibold ml-1">Start Session</span>
                            <div className="bg-white/20 rounded-full p-1 group-hover/btn:rotate-90 transition-transform duration-300">
                                <ChevronRight size={16} className="text-white" />
                            </div>
                        </Link>
                    </Button>
                </CardFooter>
            </Card>
        );
    };

    const handleTabChange = useCallback((tab: 'dashboard' | 'sessions' | 'history' | 'reports') => {
        setActiveTab(tab);
    }, [setActiveTab]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200">
            <Navbar currentPage="dashboard" />

            <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
                {renderHeader()}

                {activeTab === 'dashboard' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {renderControls()}

                        {/* Error State */}
                        {error && (
                            <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-red-700 mb-6 flex items-center gap-3">
                                <div className="bg-red-100 p-2 rounded-full"><Activity size={16} /></div>
                                <p>Failed to load active sessions. Please try refreshing.</p>
                            </div>
                        )}

                        {/* Loading Grid */}
                        {isValidating && !sessions.length && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="h-64 bg-white rounded-xl border border-gray-100 shadow-sm animate-pulse"></div>
                                ))}
                            </div>
                        )}

                        {/* Empty State */}
                        {!isValidating && sessions.length === 0 && !error && (
                            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-200 border-dashed">
                                <div className="h-16 w-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4">
                                    <Calendar size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Sessions</h3>
                                <p className="text-gray-500 max-w-sm text-center mb-6">
                                    You don't have any training sessions scheduled right now. Check back later or contact your administrator.
                                </p>
                            </div>
                        )}

                        {/* Content Grid */}
                        {!isValidating && sessions.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {sortedSessions.map((session, i) => renderSessionCard(session, i))}
                            </div>
                        )}
                    </div>
                )}

                {/* Other Tabs */}
                {activeTab === 'sessions' && hasDoctor && (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                        <SessionReportAI />
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                        <UserActivity />
                    </div>
                )}
            </main>

            <OnboardingModal
                isOpen={showOnboardingModal}
                onClose={() => setShowOnboardingModal(false)}
            />
        </div>
    );
};

export default Dashboard;
